import { useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import env from '../utils/env';

export const BACKEND_URL = env.backendUrl;

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function getApiErrorStatus(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined;
}

const handleAuthError = (authHeader: string | null): Error | null => {
  if (authHeader?.includes('error="invalid_token"')) {
    const description = /error_description="([^"]+)"/.exec(authHeader)?.[1];
    return new Error(description || 'Token is invalid or has expired. Please log in again.');
  }
  return null;
};


export default function useAPI() {
  const auth = useAuth();

  const fetchAuthenticated = useCallback(async <T>(endpoint: string, options: RequestInit = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
      Authorization: [auth.user?.token_type, auth.user?.access_token].filter(Boolean).join(' '),
    };

    const response = await fetch(`${BACKEND_URL}${endpoint}`, { ...options, headers });

    if (!response.ok) {
      if (response.headers.has('www-authenticate')) {
        const authError = handleAuthError(response.headers.get('www-authenticate'));
        if (authError) throw authError;
      }
      throw new ApiError(`HTTP error! Status: ${response.status}`, response.status);
    }

    if (response.headers.get('Content-Type')?.includes('application/json')) {
      try {
        return await response.json() as Promise<T>;
      } catch (e) {
        throw new Error('Failed to parse JSON response', { cause: e });
      }
    }

    return response.text() as unknown as T;
  }, [auth.user]);

  return useMemo(() => ({
    Get: async function Get<T>(endpoint: string) {
      return fetchAuthenticated<T>(endpoint, { method: 'GET' });
    },
    Delete: async function Delete<T>(endpoint: string) {
      return fetchAuthenticated<T>(endpoint, { method: 'DELETE' });
    },
    Post: async function Post<T>(endpoint: string, data: unknown) {
      return fetchAuthenticated<T>(endpoint, { method: 'POST', body: JSON.stringify(data) });
    },
    Put: async function Put<T>(endpoint: string, data: unknown) {
      return fetchAuthenticated<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) });
    },
  }), [fetchAuthenticated]);
}

